import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useForm from 'react-hook-form';
import { Grid, Box } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import {
  getContract,
  updateContract,
  setContract,
} from '../../../../redux/modules/recPayment';
import {
  addToBasket,
  updateBasket,
} from '../../../../redux/modules/basketReducer';
import { efesOrange } from '../../../../theme';
import MobileTable from './MobileTable';
import DataTable from '../../../../components/DataTable/DataTable';
import TitleTypography from '../../../../components/MUI/TitleTypography';
import Loader from '../../../../components/Loaders/Loader';
import MainSwitch from '../../../../components/MUI/MainSwitch';
import StepNavigationAmount from '../../../../components/MUI/StepNavigationAmount';
import MainButton from '../../../../components/MUI/MainButton';
import PolicySearchForm from '../../../../components/FormComponents/PolicySearchForm';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import useUnRegisterValidationFields from '../../../../hooks/useUnRegisterValidationFields';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import contractNumberSchema from '../../../../validations/contractNumberValidations';
import {
  desktopTableColumns,
  getContractStatus,
  maskContract,
  mobileTableColumns,
  replaceMaskedContractNumber,
} from '../../../../helpers/recurringPaymentHelper';

import useStyles from '../../makeStyles';
import recurringSearch from '../../../../styles/svgs/recurringSearch.svg';
import styles from './RecurringPaymentSchedule.module.scss';

import BasketIcon from '../../../../icons/Basket';

const RecurringPaymentSchedule = ({ params }) => {
  const {
    setStepNavigationBox,
    handleOpenMobileSummary,
    generalAmount,
    handlePayClick,
    payBtnTitle,
    payBtnAmount,
  } = params;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const urlParams = queryString.parse(search);
  const classes = useStyles();

  const { contract, isLoading } = useSelector((store) => store.recPayment, shallowEqual);

  const totalAndBalance = contract && contract.totalAndBalance;
  const selectedToPay = totalAndBalance && totalAndBalance.selectedToPay;
  const { basket } = useSelector((store) => store.basketReducer, shallowEqual);
  const selectedContract = useMemo(
    () => contract && basket.find((el) => el.policyNumber === contract.policyNumber),
    [contract, basket],
  );
  const [policyNumber, setPolicyNumber] = useState('');
  const isDeviceLgSize = useDeviceResize('lg');
  const isDeviceMdSize = useDeviceResize('md');
  const [showAllRows, setShowAllRows] = useState(false);

  const {
    handleSubmit,
    register,
    unregister,
    errors,
    clearError,
    setValue,
    triggerValidation,
  } = useForm({
    validationSchema: contractNumberSchema,
    mode: 'onBlur',
  });

  useRegisterValidationFields(['policyNumber'], register);
  useUnRegisterValidationFields(['policyNumber'], unregister);

  useEffect(() => {
    if (urlParams.policyNumber) {
      dispatch(getContract({ policyNumber: urlParams.policyNumber }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (urlParams.policyNumber) {
      const maskedContract = maskContract(urlParams.policyNumber);
      setValue('policyNumber', maskedContract, true);
    } else if (contract.policyNumber) {
      const maskedContract = maskContract(contract.policyNumber);
      setValue('policyNumber', maskedContract, true);
    }
  }, [contract.policyNumber, setValue, urlParams.policyNumber]);

  useEffect(() => {
    if (
      selectedContract
      && selectedContract.totalAndBalance.selectedToPay !== contract.totalAndBalance.selectedToPay
    ) {
      dispatch(updateBasket(contract));
    }
  }, [contract, dispatch, selectedContract]);

  const handleUpdateContract = (tableRows) => {
    dispatch(
      updateContract({
        ...contract,
        tableRows,
      }),
    );
  };

  const handleDTRowClick = (row) => {
    if (row.isDisabled) {
      return;
    }

    const lastCheckedRowId = row.isChecked && !row.isAlwaysChecked ? row.rowId - 1 : row.rowId;

    const updatedRows = contract.tableRows.map((item) => {
      if (item.isDisabled) {
        return { ...item };
      }

      return {
        ...item,
        isLastChecked: item.rowId === lastCheckedRowId,
        isChecked:
          item.rowId === row.rowId
            ? item.isAlwaysChecked || !item.isChecked
            : item.rowId < row.rowId,
        payable: item.maxPayable,
      };
    });

    handleUpdateContract(updatedRows);
  };

  const isDTVisible = useMemo(
    () => !isLoading && !contract.isFullyPaid && !isEmpty(contract.tableRows),
    [contract.isFullyPaid, contract.tableRows, isLoading],
  );

  const basketContract = basket.find((el) => el.policyNumber === contract.policyNumber);
  const dataArray = !isEmpty(contract.tableRows) ? contract.tableRows : [];
  const filteredRows = showAllRows ? dataArray : dataArray.filter((row) => !row.isDisabled);

  const handleAddToBasketClick = () => {
    if (!basketContract) {
      dispatch(addToBasket(contract));
    }
  };

  useEffect(() => {
    setStepNavigationBox(() => (
      <>
        <Grid container spacing={2} alignItems="center" onClick={handleOpenMobileSummary}>
          <StepNavigationAmount amount={generalAmount} iconDown={false} isMobile={isDeviceLgSize} />
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <MainButton
              width="fullWidth"
              variant="outlined"
              color="primary"
              onClick={() => handleAddToBasketClick()}
              startIcon={<BasketIcon color={efesOrange} />}
              disabled={!+selectedToPay || !!basketContract}
            >
              {t('recurrPayment.btns.addToBasket')}
            </MainButton>
          </Grid>
          <Grid item xs={12} md={6}>
            <MainButton
              width="fullWidth"
              variant="contained"
              color="primary"
              onClick={handlePayClick}
              disabled={!+selectedToPay || !basketContract}
            >
              {payBtnTitle}
              {!!payBtnAmount && !isDeviceLgSize && (
                <>
                  &nbsp;
                  <b>{payBtnAmount}</b>
                </>
              )}
            </MainButton>
          </Grid>
        </Grid>
      </>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeviceLgSize, handleSubmit, generalAmount, basketContract, selectedToPay]);

  const onSubmit = useCallback(
    (data) => {
      const newPolicyNumber = replaceMaskedContractNumber(data.policyNumber);

      if (newPolicyNumber === contract.policyNumber) {
        return;
      }

      const filteredBasketContract = basket.find((c) => c.policyNumber === newPolicyNumber);
      if (filteredBasketContract) {
        dispatch(setContract(filteredBasketContract));
        return;
      }

      dispatch(
        getContract({
          policyNumber: newPolicyNumber,
        }),
      );

      urlParams.policyNumber = replaceMaskedContractNumber(data.policyNumber);
      history.push({ search: queryString.stringify(urlParams) });
      setPolicyNumber('');
    },
    [contract.policyNumber, basket, dispatch, urlParams, history, setPolicyNumber],
  );

  return (
    <Box>
      <div id={styles.checkPolicy}>
        <div>
          {isLoading && (
            <div>
              <Loader />
            </div>
          )}
          <TitleTypography component="div" className={styles.headerTitle}>
            {t('recurrPayment.title')}
          </TitleTypography>
          <form className={styles.form}>
            <PolicySearchForm
              formProps={{
                handleSubmit,
                errors,
                clearError,
                setValue,
                triggerValidation,
                onSubmit,

              }}
              policyNumber={policyNumber}
              setPolicyNumber={setPolicyNumber}
              isLoading={isLoading}
              gridSize={12}

            />
            <Grid item xs={isDeviceMdSize ? 10 : 11} />
            {contract.result && (
              <div className={styles.message}>{getContractStatus(contract, t)}</div>
            )}
          </form>
          {!isDTVisible && (
            <div className={styles.infoBlock}>
              <img src={recurringSearch} alt="" />
              <div className={styles.subText}>{t('recurrPayment.info')}</div>
            </div>
          )}
        </div>
        {isDTVisible && (
          <Grid item xs={12} className={styles.infoSection}>
            <div className={styles.policyNumber}>{maskContract(contract.policyNumber)}</div>
            <div className={styles.fullName}>
              <MainSwitch
                color="primary"
                size={isDeviceLgSize ? 'small' : 'medium'}
                checked={showAllRows}
                onChange={(event) => {
                  setShowAllRows(event.target.checked);
                }}
              />
              <label className={styles.previousPayments}>
                {t('recurrPayment.previousPayments')}
              </label>
            </div>
          </Grid>
        )}
        {isDTVisible && (
          <>
            {isDeviceLgSize ? (
              <MobileTable
                data={filteredRows}
                handleRowClick={handleDTRowClick}
                columns={mobileTableColumns(t, isDeviceLgSize, classes)}
                contract={contract}
                handleUpdateContract={handleUpdateContract}
              />
            ) : (
              <DataTable
                data={filteredRows}
                columns={desktopTableColumns(contract, handleUpdateContract, t, classes)}
                loading={isLoading}
                paginated={false}
                handleRowClick={handleDTRowClick}
              />
            )}
            {!isDeviceLgSize && (
              <Button
                color="primary"
                variant="contained"
                startIcon={<BasketIcon />}
                onClick={() => handleAddToBasketClick()}
                disabled={!+selectedToPay || !!basketContract}
                className={styles.addToBasket}
              >
                {t('recurrPayment.btns.addToBasket')}
              </Button>
            )}
          </>
        )}
      </div>
    </Box>
  );
};

RecurringPaymentSchedule.propTypes = {
  params: PropTypes.shape({
    setStepNavigationBox: PropTypes.func.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    generalAmount: PropTypes.number.isRequired,
    handlePayClick: PropTypes.func.isRequired,
    payBtnTitle: PropTypes.string.isRequired,
    payBtnAmount: PropTypes.string.isRequired,
  }).isRequired,
};

export default RecurringPaymentSchedule;
